import { BuildingLibraryIcon, BuildingOffice2Icon, DocumentTextIcon } from '@heroicons/react/24/outline';

import AIIcon from 'icons/AI';
import AccountManagementIcon from 'icons/AccountManagementIcon2';
import AppsIcon from 'icons/Apps';
import CalendarIcon2 from 'icons/CalendarIcon2';
import ComplianceDocsIcon from 'icons/ComplianceDocsIcon';
import EmployeeBenefits from 'icons/EmployeeBenefits';
import Expenses from 'icons/Expenses';
import Finance from 'icons/Finance';
import HomeDashboard from 'icons/HomeDashboard';
import Invoices from 'icons/Invoices';
import Payroll from 'icons/Payroll';
import PencilUnderline from 'icons/PencilUnderline';
import Resources from 'icons/Resources';
import SecurityLockIcon from 'icons/SecurityLockIcon';
import Settings from 'icons/Settings';
import TimesheetIcon from 'icons/Timesheet';
import TimeTrackingIcon from 'icons/TimeTracking';
import TheraAdvanceIcon from 'icons/TheraAdvanceIcon';
import Transactions from 'icons/Transactions';
import Wallet from 'icons/Wallet';
import Withholdings from 'icons/Withholdings';
import Gift from 'icons/Gift';
import { ContractorRoutes, ReferralRoutes, WorkerRoutesWithAccess } from 'routes';
import { getWhiteLabelAskLabel } from 'utils/whiteLabelConfig';
import { NavItem } from '../types';

export const getWorkerNavigation = (): NavItem[] => [
  {
    name: 'Home',
    icon: <HomeDashboard />,
    href: ContractorRoutes.Dashboard,
  },
  {
    name: 'Payroll',
    onlyShownTo: ['contractor'],
    icon: <Payroll />,
    href: ContractorRoutes.Contracts,
    subNavs: [
      {
        name: 'Contracts',
        href: ContractorRoutes.Contracts,
        isShownInBar: true,
        icon: <PencilUnderline className="h-6 w-6" />,
        subNavs: [
          {
            name: 'Contract Details',
            href: ContractorRoutes.ContractDetails,
            isShownInBar: false,
          },
        ],
      },
      {
        name: 'Invoices',
        href: ContractorRoutes.InvoiceHistory,
        isShownInBar: true,
        icon: <Invoices className="h-6 w-6" />,
      },
      {
        name: 'Withdrawals',
        href: ContractorRoutes.Withdrawals,
        isShownInBar: true,
        icon: <Transactions className="h-5 w-5" />,
        subNavs: [{ name: 'Withdrawal Info', href: ContractorRoutes.WithdrawalInfo, isShownInBar: false }],
      },
      {
        name: 'Thera Advance',
        href: ContractorRoutes.TheraAdvance,
        isShownInBar: true,
        icon: <TheraAdvanceIcon className="h-6 w-6" />,
        onlyShownTo: ['contractor', 'isTheraAdvanceEligible', 'isTheraAdvanceCompany', 'isFastPaymentsDisabled'],
        subNavs: [{ name: 'Transaction Detail', href: ContractorRoutes.TheraAdvanceTransaction, isShownInBar: false }],
      },
      {
        name: 'Wallet',
        href: ContractorRoutes.Wallet,
        isShownInBar: true,
        icon: <Wallet className="h-5 w-5" />,
        onlyShownTo: WorkerRoutesWithAccess.Wallet?.access,
        subNavs: [{ name: 'Wallet Withdrawal Info', href: ContractorRoutes.WalletWithdrawalInfo, isShownInBar: false }],
        isNew: true,
      },
      {
        name: 'Time Off',
        href: ContractorRoutes.TimeOff,
        isShownInBar: true,
        icon: <CalendarIcon2 className="ml-0.5 mt-0.5 h-5 w-5" />,
      },
      {
        name: 'Time Tracking',
        href: ContractorRoutes.TimeTracking,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.TimeTracking?.access,
        icon: <TimeTrackingIcon className="h-6 w-6" />,
      },
      {
        name: 'Timesheet',
        href: ContractorRoutes.Timesheet,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.Timesheet?.access,
        icon: <TimesheetIcon className="h-6 w-6" />,
      },
      {
        name: 'Compliance',
        href: ContractorRoutes.ComplianceDocs,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.ComplianceDocs?.access,
        icon: <ComplianceDocsIcon className="h-6 w-6" />,
      },
    ],
  },
  {
    name: 'Payroll',
    onlyShownTo: WorkerRoutesWithAccess.EOREmployeePayslips.access,
    icon: <Payroll />,
    href: ContractorRoutes.EOREmployeePayslips,
    subNavs: [
      {
        name: 'Employment',
        href: ContractorRoutes.Contracts,
        isShownInBar: true,
        icon: <PencilUnderline className="h-6 w-6" />,
        subNavs: [
          {
            name: 'Contract Details',
            href: ContractorRoutes.ContractDetails,
            isShownInBar: false,
          },
        ],
      },
      {
        name: 'Payslip',
        isShownInBar: true,
        href: ContractorRoutes.EOREmployeePayslips,
        icon: <Payroll />,
      },
    ],
  },
  {
    name: 'Payroll',
    onlyShownTo: WorkerRoutesWithAccess.EmployeePaystubs.access,
    icon: <Payroll />,
    href: ContractorRoutes.EmployerProfile,
    subNavs: [
      {
        name: 'Employment',
        href: ContractorRoutes.EmployerProfile,
        isShownInBar: true,
        icon: <PencilUnderline className="h-6 w-6" />,
      },
      {
        name: 'Paystubs',
        href: ContractorRoutes.EmployeePaystubs,
        onlyShownTo: WorkerRoutesWithAccess.EmployeePaystubs?.access,
        isShownInBar: true,
        icon: <Invoices className="h-6 w-6" />,
      },
      {
        name: 'Benefits',
        href: ContractorRoutes.EmployeeBenefits,
        onlyShownTo: WorkerRoutesWithAccess.EmployeeBenefits?.access,
        isShownInBar: true,
        icon: <EmployeeBenefits className="h-6 w-6" />,
      },
      {
        name: 'Withholdings',
        href: ContractorRoutes.EmployeeWithholdings,
        onlyShownTo: WorkerRoutesWithAccess.EmployeeWithholdings?.access,
        isShownInBar: true,
        icon: <Withholdings className="h-4 w-4" />,
        subNavs: [
          {
            name: 'Update Withholdings',
            href: ContractorRoutes.EmployeeUpdateWithholdings,
            isShownInBar: false,
          },
        ],
      },
      {
        name: 'Time Off',
        href: ContractorRoutes.TimeOff,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.TimeOff?.access,
        icon: <CalendarIcon2 className="ml-0.5 mt-0.5 h-5 w-5" />,
      },
      {
        name: 'Time Tracking',
        href: ContractorRoutes.TimeTracking,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.TimeTracking?.access,
        icon: <TimeTrackingIcon className="h-6 w-6" />,
      },
      {
        name: 'Timesheet',
        href: ContractorRoutes.Timesheet,
        isShownInBar: true,
        onlyShownTo: WorkerRoutesWithAccess.Timesheet?.access,
        icon: <TimesheetIcon className="h-6 w-6" />,
      },
      {
        name: 'Expenses',
        href: ContractorRoutes.Expenses,
        isShownInBar: true,
        icon: <Expenses className="h-5 w-5" />,
      },
      {
        name: 'Banks',
        href: ContractorRoutes.EmployeeBankAccounts,
        onlyShownTo: WorkerRoutesWithAccess.EmployeeBankAccounts?.access,
        isShownInBar: true,
        icon: <BuildingLibraryIcon className="h-5 w-5" />,
      },
      {
        name: 'Documents',
        href: ContractorRoutes.EmployeeDocuments,
        onlyShownTo: WorkerRoutesWithAccess.EmployeeDocuments?.access,
        isShownInBar: true,
        icon: <DocumentTextIcon className="h-6 w-6" />,
      },
    ],
  },
  {
    name: 'Resources',
    onlyShownTo: WorkerRoutesWithAccess.WithdrawalMethods?.access,
    href: ContractorRoutes.WithdrawalMethods,
    icon: <Resources />,
    showInMainNav: false,
    subNavs: [
      {
        name: 'Withdrawal Methods',
        href: ContractorRoutes.WithdrawalMethods,
        isShownInBar: true,
        icon: <AccountManagementIcon className="h-6 w-6" />,
      },
    ],
  },
  { name: 'Finance', href: ContractorRoutes.Finance, icon: <Finance />, showInMainNav: true },
  { name: 'Apps', href: ContractorRoutes.Apps, icon: <AppsIcon /> },

  {
    name: getWhiteLabelAskLabel(),
    href: ContractorRoutes.AI,
    icon: <AIIcon className="h-6 w-6" />,
    showInMainNav: true,
    isBeta: true,
    onlyShownTo: WorkerRoutesWithAccess.AI.access,
  },
  {
    name: '',
    icon: <Settings />,
    href: ContractorRoutes.AccountPreferences,
    subNavs: [
      {
        name: 'General',
        href: ContractorRoutes.AccountPreferences,
        isShownInBar: true,
        icon: <BuildingOffice2Icon className="h-5 w-5" />,
      },
      {
        name: 'Security',
        icon: <SecurityLockIcon className="h-5 w-5" />,
        href: ContractorRoutes.SecuritySettings,
        isShownInBar: true,
      },
      {
        name: 'Referrals',
        icon: <Gift className="h-5 w-5" />,
        href: ReferralRoutes.Portal,
        isShownInBar: true,
      },
    ],
    showInMainNav: false,
    isTabbedQuery: true,
  },
];
